import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { message, Spin, Card, Typography } from 'antd';
import { FileOutlined, CheckCircleOutlined, CloseCircleOutlined } from '@ant-design/icons';
import apiClient from '../services/api';
import { useTranslation } from 'react-i18next';

const { Title, Text } = Typography;

/**
 * SourceDownload 组件
 * 功能：下载笔记源文件（支持 uploads/ 和 cloud/ 路径）
 * 使用方式：通过 URL 参数传递文件路径，例如：/source-download?path=uploads/file.pdf
 * 显示：在新窗口中显示下载状态的卡片
 */
const SourceDownload = () => {
  const { t } = useTranslation(['dashboard', 'messages']);
  const [searchParams] = useSearchParams();
  const [isDownloading, setIsDownloading] = useState(true);
  const [downloadStatus, setDownloadStatus] = useState('downloading'); // 'downloading', 'success', 'error'
  const [fileName, setFileName] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    const downloadFile = async () => {
      const filePath = searchParams.get('path');
      
      if (!filePath) {
        setErrorMessage(t('messages:file_path_missing', { defaultValue: 'File path parameter is missing' }));
        setDownloadStatus('error');
        setIsDownloading(false);
        return;
      }

      // 验证路径格式
      if (!filePath.startsWith('uploads/') && !filePath.startsWith('cloud/')) {
        setErrorMessage(t('messages:invalid_file_path', { defaultValue: 'Invalid file path format, only paths starting with uploads/ or cloud/ are supported' }));
        setDownloadStatus('error');
        setIsDownloading(false);
        return;
      }

      // 从路径中提取文件名用于显示
      const extractedFileName = filePath.split('/').pop() || 'download';
      setFileName(extractedFileName);

      try {
        console.log('[SourceDownload] 开始下载文件:', filePath);
        
        // 调用后端API下载文件
        const response = await apiClient.post(
          '/notes/get-source-file',
          { filePath },
          {
            responseType: 'blob', // 重要：告诉 axios 期望二进制数据
          }
        );

        // 从响应头获取文件名，如果没有则从路径中提取
        const contentDisposition = response.headers['content-disposition'];
        let filename = extractedFileName;
        
        if (contentDisposition) {
          const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
          if (filenameMatch && filenameMatch[1]) {
            filename = filenameMatch[1].replace(/['"]/g, '');
            // 处理 URL 编码的文件名
            try {
              filename = decodeURIComponent(filename);
            } catch (e) {
              console.warn('[SourceDownload] 文件名解码失败，使用原始值');
            }
          }
        }

        setFileName(filename);

        // 创建 Blob 对象
        const blob = new Blob([response.data]);
        const url = window.URL.createObjectURL(blob);
        
        // 创建下载链接并触发下载
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        
        // 清理
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        console.log('[SourceDownload] 文件下载成功:', filename);
        message.success(t('messages:file_download_success', { defaultValue: 'File downloaded successfully' }));
        
        setDownloadStatus('success');
        setIsDownloading(false);
      } catch (error) {
        console.error('[SourceDownload] 文件下载失败:', error);
        const errorMsg = error.response?.data?.message || error.message || t('messages:file_download_failed', { defaultValue: 'File download failed' });
        setErrorMessage(errorMsg);
        message.error(`${t('messages:file_download_failed', { defaultValue: 'File download failed' })}: ${errorMsg}`);
        
        setDownloadStatus('error');
        setIsDownloading(false);
      }
    };

    downloadFile();
  }, [searchParams, t]);

  const getStatusIcon = () => {
    switch (downloadStatus) {
      case 'downloading':
        return <Spin size="large" />;
      case 'success':
        return <CheckCircleOutlined style={{ fontSize: '48px', color: '#52c41a' }} />;
      case 'error':
        return <CloseCircleOutlined style={{ fontSize: '48px', color: '#ff4d4f' }} />;
      default:
        return <FileOutlined style={{ fontSize: '48px', color: '#1890ff' }} />;
    }
  };

  const getStatusText = () => {
    switch (downloadStatus) {
      case 'downloading':
        return t('messages:downloading_file', { defaultValue: 'Downloading file...' });
      case 'success':
        return t('messages:download_completed', { defaultValue: 'Download completed' });
      case 'error':
        return t('messages:download_failed', { defaultValue: 'Download failed' });
      default:
        return '';
    }
  };

  return (
    <div style={{ 
      display: 'flex', 
      justifyContent: 'center', 
      alignItems: 'center', 
      minHeight: '100vh',
      padding: '20px',
      backgroundColor: '#f0f2f5'
    }}>
      <Card
        style={{
          width: '100%',
          maxWidth: '500px',
          textAlign: 'center',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
        }}
      >
        <div style={{ 
          display: 'flex', 
          flexDirection: 'column',
          alignItems: 'center',
          gap: '24px',
          padding: '20px 0'
        }}>
          {/* 图标区域 */}
          <div style={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center',
            minHeight: '80px'
          }}>
            {getStatusIcon()}
          </div>

          {/* 标题 */}
          <Title level={4} style={{ margin: 0 }}>
            {t('dashboard:source_file_download', { defaultValue: 'Source File Download' })}
          </Title>

          {/* 文件名 */}
          {fileName && (
            <div style={{ 
              width: '100%',
              padding: '12px',
              backgroundColor: '#fafafa',
              borderRadius: '4px',
              wordBreak: 'break-all'
            }}>
              <FileOutlined style={{ marginRight: '8px', color: '#1890ff' }} />
              <Text strong>{fileName}</Text>
            </div>
          )}

          {/* 状态文本 */}
          <Text style={{ 
            fontSize: '16px', 
            color: downloadStatus === 'error' ? '#ff4d4f' : downloadStatus === 'success' ? '#52c41a' : '#666'
          }}>
            {getStatusText()}
          </Text>

          {/* 错误信息 */}
          {downloadStatus === 'error' && errorMessage && (
            <div style={{ 
              width: '100%',
              padding: '12px',
              backgroundColor: '#fff2f0',
              borderRadius: '4px',
              border: '1px solid #ffccc7'
            }}>
              <Text type="danger" style={{ fontSize: '14px' }}>
                {errorMessage}
              </Text>
            </div>
          )}

          {/* 成功提示 */}
          {downloadStatus === 'success' && (
            <div style={{ 
              width: '100%',
              padding: '12px',
              backgroundColor: '#f6ffed',
              borderRadius: '4px',
              border: '1px solid #b7eb8f'
            }}>
              <Text style={{ color: '#52c41a', fontSize: '14px' }}>
                {t('messages:file_downloaded_successfully', { defaultValue: 'File has been successfully downloaded to your device' })}
              </Text>
            </div>
          )}

          {/* 关闭按钮提示 */}
          {downloadStatus === 'success' && (
            <Text type="secondary" style={{ fontSize: '12px' }}>
              {t('messages:you_can_close_window', { defaultValue: 'You can close this window' })}
            </Text>
          )}
        </div>
      </Card>
    </div>
  );
};

export default SourceDownload;

